'use client'

import {
  CLUSTER_CONFIG,
  CLUSTER_KEYS,
  CLUSTER_KEY_BY_LABEL,
  clusterKeyForOpportunity,
  matchOpportunityToCluster,
} from '@/config/market-config'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/ui/atoms/dropdown/DropdownMenu'
import { cn } from '@/ui/utils/style'
import { getVaultMarket, HYPEREVM_PRODUCTS } from '@/features/evault-details/data/products'
import type { Address } from 'viem'
import type { NormalizedELendInfo } from '@/domain/euler/elends/types'
import type { ProcessedActivity } from '../logic/useActivity'
import { ExpandableSearch } from '@/ui/atoms/expandable-search/ExpandableSearch'
import { SlidersHorizontal } from 'lucide-react'

/**
 * Activity-page filter chip label. Derived from `CLUSTER_CONFIG` so adding a
 * new cluster (or renaming one) propagates automatically — including `Scale`,
 * which was previously missing from this list.
 */
export type ActivityMarketFilter = string

export const ACTIVITY_MARKET_FILTERS: ActivityMarketFilter[] = CLUSTER_KEYS.map(
  (key) => CLUSTER_CONFIG[key]!.name,
)

interface ActivityFilterBarProps {
  selectedMarkets: ActivityMarketFilter[]
  onToggleMarket: (market: ActivityMarketFilter) => void
  onClearFilters: () => void
  searchQuery: string
  onSearchChange: (query: string) => void
}

export function ActivityFilterBar({
  selectedMarkets,
  onToggleMarket,
  onClearFilters,
  searchQuery,
  onSearchChange,
}: ActivityFilterBarProps) {
  const isAllSelected = selectedMarkets.length === 0
  const activeFilterCount = selectedMarkets.length

  return (
    <div className="sticky top-12 z-20 flex flex-wrap items-center justify-between gap-2 bg-background py-2 sm:static sm:gap-4 sm:bg-transparent sm:py-0">
      {/* Left side: filter pills (desktop) / dropdown (mobile) */}
      <div className="flex items-center gap-2 sm:flex-wrap">
        {/* Desktop pills */}
        <div className="hidden sm:block">
          <FilterPill selected={isAllSelected} onClick={onClearFilters}>
            All
          </FilterPill>
        </div>
        <div className="mx-1 hidden h-4 w-px shrink-0 bg-white/10 sm:block" />
        <div className="hidden items-center gap-2 sm:flex">
          {ACTIVITY_MARKET_FILTERS.map((market) => (
            <FilterPill key={market} selected={selectedMarkets.includes(market)} onClick={() => onToggleMarket(market)}>
              {market}
            </FilterPill>
          ))}
        </div>

        {/* Mobile dropdown */}
        <div className="sm:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  'relative flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 font-medium text-sm transition-colors',
                  activeFilterCount > 0
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground',
                )}
              >
                <SlidersHorizontal className="h-3.5 w-3.5" />
                <span>Filters</span>
                {activeFilterCount > 0 && (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-white/20 text-xs">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48 p-3">
              <div className="flex flex-col gap-3">
                <div className="font-medium text-muted-foreground text-xs uppercase tracking-wider">Markets</div>
                <div className="flex flex-wrap gap-1.5">
                  <FilterPill selected={isAllSelected} onClick={onClearFilters} size="sm">
                    All
                  </FilterPill>
                  {ACTIVITY_MARKET_FILTERS.map((market) => (
                    <FilterPill
                      key={market}
                      selected={selectedMarkets.includes(market)}
                      onClick={() => onToggleMarket(market)}
                      size="sm"
                    >
                      {market}
                    </FilterPill>
                  ))}
                </div>
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Right side: search */}
      <div className="flex items-center gap-2">
        <ExpandableSearch value={searchQuery} onChange={onSearchChange} />
      </div>
    </div>
  )
}

interface FilterPillProps {
  children: React.ReactNode
  selected: boolean
  onClick: () => void
  size?: 'sm' | 'default'
}

function FilterPill({ children, selected, onClick, size = 'default' }: FilterPillProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'shrink-0 rounded-full font-medium transition-colors',
        size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm',
        selected
          ? 'bg-primary text-primary-foreground'
          : 'bg-white/5 text-muted-foreground hover:bg-white/10 hover:text-foreground',
      )}
    >
      {children}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Filtering logic
// ---------------------------------------------------------------------------

function getEulerMarketKey(
  activity: ProcessedActivity,
  indexerVaults?: Map<Address, NormalizedELendInfo>,
): string | undefined {
  if (activity.vaultAddress) {
    const addr = activity.vaultAddress as Address
    return getVaultMarket(addr, indexerVaults?.get(addr)?.products)?.key
  }
  return undefined
}

/**
 * Translate a ProcessedActivity into the shape expected by
 * `matchOpportunityToCluster`. The activity's `type` field (`'euler' |
 * 'pooled' | 'isolated'`) doesn't distinguish evault from elend clusters, so
 * we disambiguate via the vault's HYPEREVM_PRODUCTS entry. `address` is
 * forwarded so evault-cluster matching (which uses vault address membership)
 * works.
 */
function activityAsOpportunity(
  activity: ProcessedActivity,
  indexerVaults?: Map<Address, NormalizedELendInfo>,
): { source: string; market?: { key?: string }; address?: string } {
  if (activity.type === 'pooled') return { source: 'pooled' }
  if (activity.type === 'isolated') return { source: 'isolated' }
  // activity.type === 'euler' — could be an elend cluster or an evault (Earn) vault
  const marketKey = getEulerMarketKey(activity, indexerVaults)
  const product = marketKey ? HYPEREVM_PRODUCTS.get(marketKey) : undefined
  const source = product?.source === 'evault' ? 'evault' : 'elend'
  return { source, market: { key: marketKey }, address: activity.vaultAddress }
}

/**
 * Returns true if the activity matches at least one of the selected market filters.
 * When `selectedMarkets` is empty, all activities pass (= "All").
 *
 * Fully data-driven: a new cluster added to `HYPEREVM_PRODUCTS` auto-registers
 * a filter chip here and matching just works.
 */
export function activityMatchesMarketFilter(
  activity: ProcessedActivity,
  selectedMarkets: ActivityMarketFilter[],
  indexerVaults?: Map<Address, NormalizedELendInfo>,
): boolean {
  if (selectedMarkets.length === 0) return true

  const opp = activityAsOpportunity(activity, indexerVaults)
  const activityClusterKey = clusterKeyForOpportunity(opp)
  if (!activityClusterKey) return false

  return selectedMarkets.some((market) => {
    const targetKey = CLUSTER_KEY_BY_LABEL.get(market)
    return targetKey !== undefined && matchOpportunityToCluster(opp, targetKey)
  })
}
